import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { FaGoogle, FaFacebook, FaTwitter, FaGithub } from 'react-icons/fa';
import { Provider } from '@supabase/supabase-js';

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      navigate('/confirm');
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: Provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
      });
      if (error) throw error;
    } catch (error: any) {
      const errorMessage = t(`errors.${provider.toLowerCase()}SignInFailed`, error.message);
      setError(typeof errorMessage === 'string' ? errorMessage : error.message);
    }
  };

  // Define colors for the social login icons
  const iconColors: { [key: string]: string } = {
    google: '#DB4437',
    github: '#333333',
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200 p-4 overflow-y-auto">
      <div className="w-full max-w-md bg-base-100 p-8 rounded shadow">
        <h1 className="text-3xl font-bold mb-6 text-center">
          {t('register.title')}
        </h1>
        
        {/* Social Login Buttons */}
        <div className="flex justify-center space-x-4 mb-6">
          <button 
            onClick={() => handleSocialLogin('github')} 
            className="btn btn-outline gap-2"
            disabled={isLoading}
          >
            <FaGithub size={20} style={{ color: iconColors.github }} />
            <span>{t('register.github')}</span>
          </button>
        </div>

        <div className="my-6 flex items-center">
          <div className="flex-grow border-t border-base-content"></div>
          <span className="mx-4 text-sm text-base-content">
            {t('register.or')}
          </span>
          <div className="flex-grow border-t border-base-content"></div>
        </div>

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-base-content">
              {t('register.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input input-bordered w-full"
              placeholder={t('register.emailPlaceholder')}
              disabled={isLoading}
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-base-content">
              {t('register.password')}
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input input-bordered w-full"
              placeholder={t('register.passwordPlaceholder')}
              disabled={isLoading}
            />
          </div>
          {error && (
            <div className="alert alert-error">
              <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{error}</span>
            </div>
          )}
          <button 
            type="submit" 
            className={`btn btn-primary w-full mt-4 ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? t('common.loading') : t('register.signUp')}
          </button>
        </form>

        <p className="mt-4 text-center text-sm">
          <a href="/login" className="link link-primary">
            {t('register.alreadyAccount')}
          </a>
        </p>
      </div>
    </div>
  );
}